import { Paper, Table, TableBody, TableContainer } from '@mui/material';
import React, { useState } from 'react';
import {
  ValueAgentRow,
  ValueAgentsChipRow,
  ValueChipRow,
  ValueContactListRow,
  ValueDateRow,
  ValueRow,
} from '../../../../../components/table';
import { FloatingActionsButtons } from '../../../../../components/FloatingActionsButtons';
import { UpsertClientDialog } from '../../../dialogs/UpsertClientDialog';
import { DeleteClientDialog } from '../../../dialogs/DeleteClientDialog';
import { useTranslation } from 'react-i18next';
import { Status } from '../../../../../api/clientsApi/interfaces';
import { DeleteAction } from '../../../../../components/FloatingActionsButtons/components/DeleteAction';
import { EditAction } from '../../../../../components/FloatingActionsButtons/components/EditAction';
import { useDeleteClient } from '../../../../../reactQuery/clients';
import { enqueueSnackbar } from 'notistack';
import { useNavigate } from 'react-router';
import { TabPanel } from '../../../../../components/tabs';
import { IDetailsTabPanel } from './interfaces';

export const DetailsTabPanel: React.FC<IDetailsTabPanel> = ({
  index,
  value,
  client,
}): JSX.Element => {
  const { t } = useTranslation();

  const { mutate: deleteClient, isLoading: isDeletingClient } =
    useDeleteClient();

  const navigate = useNavigate();

  const [showEditDialog, setShowEditDialog] = useState<boolean>(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);

  const onDeleteClient = (): void => {
    deleteClient(client.id, {
      onSuccess: () => {
        enqueueSnackbar(t('clients:prompts.deleteClient.success'), {
          variant: 'success',
        });
        navigate('/clients');
      },
      onError: () => {
        enqueueSnackbar(t('clients:prompts.deleteClient.failed'), {
          variant: 'error',
        });
      },
    });
  };

  return (
    <TabPanel value={value} index={index}>
      <TableContainer component={Paper}>
        <Table>
          <TableBody>
            <ValueRow label={t('entities:client.name')} value={client.name} />
            <ValueRow
              label={t('entities:client.sector')}
              value={client.sector.name}
            />

            {client.sector.hasRooms && (
              <ValueRow
                label={t('entities:client.numberOfRooms')}
                value={client.numberOfRooms}
              />
            )}

            {client.sector.hasMealPlan && (
              <ValueChipRow
                label={t('entities:client.mealPlan')}
                value={client.mealPlan.map((mealPlan) => mealPlan.name)}
              />
            )}

            <ValueRow
              label={t('entities:client.island')}
              value={client.island.name}
            />
            <ValueRow
              label={t('entities:client.area')}
              value={client?.area?.name}
            />
            <ValueRow
              label={t('entities:client.group')}
              value={client?.group?.name}
            />
            <ValueRow
              label={t('entities:client.status')}
              value={t(`entities:client.enums.status.${client.status}`)}
            />

            {client.status === Status.ACTIVE && (
              <ValueChipRow
                label={t('entities:client.contractedProducts')}
                value={client.contractedProducts?.map(
                  (product) => product.name
                )}
              />
            )}

            {(client.status === Status.ACTIVE ||
              client.status === Status.POTENTIAL) && (
              <ValueChipRow
                label={t('entities:client.potentialProducts')}
                value={client.potentialProducts?.map((product) => product.name)}
              />
            )}

            <ValueContactListRow
              label={t('entities:client.contacts')}
              contacts={client.contacts}
            />

            <ValueRow
              label={t('entities:client.contactInterval')}
              value={
                client.contactInterval
                  ? t('words.month', { count: client.contactInterval / 30 })
                  : t('forms:createClient.fields.contactInterval.noInterval')
              }
            />

            <ValueAgentsChipRow
              label={t('entities:client.responsibleAgents')}
              agents={client.responsibleAgents}
            />

            <ValueAgentRow
              agent={client.createdBy}
              label={t('entities:common.createdBy')}
            />

            <ValueDateRow
              date={client.createdAt}
              label={t('entities:common.createdAt')}
            />

            <ValueDateRow
              date={client.updatedAt}
              label={t('entities:common.updatedAt')}
            />
          </TableBody>
        </Table>
      </TableContainer>

      <FloatingActionsButtons>
        <DeleteAction
          onClick={() => {
            setShowDeleteDialog(true);
          }}
        />
        <EditAction
          onClick={() => {
            setShowEditDialog(true);
          }}
        />
      </FloatingActionsButtons>

      <UpsertClientDialog
        client={client}
        show={showEditDialog}
        onClose={() => setShowEditDialog(false)}
      />
      <DeleteClientDialog
        show={showDeleteDialog}
        clientName={client.name}
        disabled={isDeletingClient}
        onConfirm={onDeleteClient}
        onClose={() => setShowDeleteDialog(false)}
      />
    </TabPanel>
  );
};
